'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import useFetchCollection from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import { getContainer } from '@framework/di/getContainer';
import PortfolioMonitorRepository from '@/creditflowresearch/infrastructure/repository/PortfolioMonitorRepository';
import { usePortfoliosContext } from '@/creditflowresearch/application/PortfolioMonitor/PortfoliosContext';
import {
  IPositionItem, IPositionsResponse,
} from '@/creditflowresearch/domain/vo/PortfolioMonitor/IPositionsCollection';
import { useRouter } from '@framework/router';
import { addQueryParams, cleanQueryParams } from '@framework/router/nextRouter';
import useMySelection from '@/creditflowresearch/ui/common/hooks/useMySelection';
import { IDatetime } from '@framework/date-helpers/Datetime';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { useCancelChangesModal } from '@/creditflowresearch/ui/common/CancelChangesModal';

export const usePositions = () => {
  const portfolioMonitorRepository = getContainer()
      .get<PortfolioMonitorRepository>(PortfolioMonitorRepository);
  const [lastUpdated, setLastUpdated] = useState<IDatetime>();
  const { selectedPortfolioList, selectedPortfolio } = usePortfoliosContext();
  const { push, query } = useRouter();
  const { q = '' }: { q?: string } = query;
  const [search, setSearch] = useState<string>(q);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchPositions = useCallback(async (payload) => {
    const response: IPositionsResponse = await portfolioMonitorRepository.getPositionsByList(payload);
    setLastUpdated(response.lastUpdatedAt);
    return response.positions;
  }, [selectedPortfolioList]);

  const positionList = useFetchCollection<IPositionItem>(
      fetchPositions,
      {
        enabled: !!selectedPortfolioList?.id,
        payload: {
          listId: selectedPortfolioList?.id || null,
        },
        initialValues: {
          sortBy: 'name',
          sortOrder: 'asc',
          debouncedSearchString: search,
        },
      },
  );

  useEffect(() => positionList.reset, []);

  const onSearch = (value:string) => {
    setSearch(value);
    positionList.debouncedSearch(value);
  };

  const onSetSearchUrlParam = (search) => {
    const newQuery = addQueryParams(query, { q: search });

    push({ query: cleanQueryParams(newQuery) }, undefined, { shallow: true });
  };

  const selection = useMySelection((item: string) => item);

  useEffect(() => {
    if (selectedPortfolio?.id) {
      selection.reset();
    }
  }, [selectedPortfolio?.id]);

  const confirmDeletePositionsModal = useCancelChangesModal();

  const confirmDeletePositions = async () => {
    setIsLoading(true);
    confirmDeletePositionsModal.hideCanceledModal();
    await bulkDeletePositions(selection.collection);
    await positionList.getCollection();
    selection.reset();
    setIsLoading(false);
  };

  const bulkDeletePositions = async (cusips: string[]) => {
    const payload = {
      portfolioId: selectedPortfolio.id,
      listId: selectedPortfolioList.id,
      cusips,
    };
    try {
      await portfolioMonitorRepository.deletePositions(payload);

      AlertManager.success(`${ cusips.length } position${ cusips.length > 1 ? 's have' : ' has' } been deleted.`);
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    }
  };

  return {
    positionList,
    lastUpdated,
    search,
    onSearch,
    onSetSearchUrlParam,
    selection,
    confirmDeletePositionsModal,
    confirmDeletePositions,
    isLoading,
  };
};

type PositionsContextType = ReturnType<typeof usePositions>;

export const PositionsContext = createContext<PositionsContextType>(null);

export const usePositionsContext = () => {
  const context = useContext(PositionsContext);
  if (!context) {
    throw new Error('usePositionsContext must be used within the PositionsContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const PositionsContextProvider = ({ children }: IProps) => (
  <PositionsContext.Provider value={ usePositions() }>
    { children }
  </PositionsContext.Provider>
);

export default PositionsContextProvider;
